import { useState, useMemo } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Scissors, CheckCircle2, Loader2, Search } from "lucide-react";

/**
 * "Bulk Add Services" — reverse of BulkQualifyDialog. Shown on each staff
 * profile. Lists every service (grouped by category) with checkboxes so the
 * admin can qualify a staff member for many services at once — especially
 * handy during onboarding. Services the staff is already qualified for are
 * marked ticked-and-locked.
 */
export function BulkServicesForStaffDialog({ staff, allServices, existingQualifications, onDone, onClose }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  const existingIds = useMemo(
    () => new Set((existingQualifications || []).map(q => q.service_id)),
    [existingQualifications]
  );

  const newSelections = [...selectedIds].filter(id => !existingIds.has(id));

  // Group services by category, filtered by search
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (allServices || []).filter(s => {
      if (!q) return true;
      return (s.name || "").toLowerCase().includes(q)
        || (s.category || "").toLowerCase().includes(q);
    });
    const byCat = {};
    for (const s of list) {
      const cat = s.category || "Other";
      (byCat[cat] = byCat[cat] || []).push(s);
    }
    return byCat;
  }, [allServices, search]);

  const toggle = (sid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  const selectAllInCategory = (svcs) => {
    const newIds = svcs.filter(s => !existingIds.has(s.service_id)).map(s => s.service_id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allTicked = newIds.every(id => next.has(id));
      if (allTicked) {
        for (const id of newIds) next.delete(id);
      } else {
        for (const id of newIds) next.add(id);
      }
      return next;
    });
  };

  const submit = async () => {
    if (newSelections.length === 0) {
      toast.info("Tick at least one new service");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/staff/${staff.user_id}/bulk-qualify-services`, {
        service_ids: newSelections,
      });
      toast.success(`Added ${res.data?.qualified_count || newSelections.length} services for ${staff.name}`);
      if (onDone) await onDone();
      onClose();
    } catch (err) {
      console.error("Bulk add services failed", err);
      toast.error(err.response?.data?.detail || "Failed to add services");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="bulk-services-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center flex-shrink-0">
              <Scissors className="w-5 h-5 text-rose-600" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <DialogTitle className="font-['Outfit'] text-xl tracking-tight">
                Add services for {staff.name}
              </DialogTitle>
              <DialogDescription className="text-xs font-['Manrope'] mt-1 leading-relaxed">
                Tick every service this staff member can perform. Base price and duration are used — you can fine-tune per-staff custom pricing below after saving.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search services by name or category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm font-['Manrope'] rounded-full"
            data-testid="bulk-services-search"
          />
        </div>

        {Object.keys(grouped).length === 0 ? (
          <div className="text-center py-6 px-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-500 font-['Manrope']" data-testid="bulk-services-empty">
            No matches.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="bulk-services-list">
            {Object.entries(grouped).map(([cat, svcs]) => {
              const newSvcsInCat = svcs.filter(s => !existingIds.has(s.service_id));
              const allTicked = newSvcsInCat.length > 0 && newSvcsInCat.every(s => selectedIds.has(s.service_id));
              return (
                <div key={cat}>
                  <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-100">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 font-['Manrope']">{cat}</p>
                    {newSvcsInCat.length > 0 && (
                      <button
                        type="button"
                        onClick={() => selectAllInCategory(svcs)}
                        className="text-[10px] font-semibold uppercase tracking-wider text-rose-600 hover:text-rose-700 font-['Manrope']"
                        data-testid={`bulk-services-cat-toggle-${cat}`}
                      >
                        {allTicked ? "Clear all" : "Select all"}
                      </button>
                    )}
                  </div>
                  {svcs.map(s => {
                    const isAlready = existingIds.has(s.service_id);
                    const isSelected = selectedIds.has(s.service_id);
                    return (
                      <button
                        key={s.service_id}
                        type="button"
                        onClick={() => !isAlready && toggle(s.service_id)}
                        disabled={isAlready}
                        data-testid={`bulk-services-row-${s.service_id}`}
                        className={`w-full flex items-center justify-between px-4 py-3 transition text-left
                          ${isAlready ? "bg-emerald-50/40 cursor-default" : "hover:bg-slate-50 cursor-pointer"}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition
                            ${isAlready ? "bg-emerald-500 border-emerald-500" :
                              isSelected ? "bg-slate-900 border-slate-900" : "bg-white border-slate-300"}`}>
                            {(isAlready || isSelected) && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate font-['Manrope']">{s.name}</p>
                            <p className="text-[11px] text-slate-400">${s.price} · {s.duration}min</p>
                          </div>
                        </div>
                        {isAlready && (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 font-['Manrope']">Already qualified</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate-400 font-['Manrope'] mr-auto" data-testid="bulk-services-count">
            {newSelections.length === 0 ? "No new services selected" :
              `${newSelections.length} new service${newSelections.length === 1 ? "" : "s"} to add`}
          </p>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="bulk-services-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || newSelections.length === 0}
            data-testid="bulk-services-submit"
            className="bg-rose-600 text-white rounded-full hover:bg-rose-700 disabled:opacity-50"
          >
            {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Saving…</> : `Add ${newSelections.length || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
